import { ReactElement, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  articlesAPI,
  ArticleListResponse,
  ArticleSummary,
} from '../../services/api';
import {
  ArrowLeft,
  Loader2,
  FileText,
  ChevronRight,
  ChevronLeft,
  BarChart3,
  DollarSign,
  LineChart,
  Users,
  Globe,
} from 'lucide-react';

// Category configuration
const CATEGORY_CONFIG: Record<string, { name: string; icon: ReactElement; gradient: string }> = {
  overview: {
    name: 'Overview',
    icon: <FileText className="w-5 h-5" />,
    gradient: 'from-emerald-500 to-cyan-500',
  },
  fundamentals: {
    name: 'Fundamentals',
    icon: <BarChart3 className="w-5 h-5" />,
    gradient: 'from-blue-500 to-indigo-500',
  },
  valuation: {
    name: 'Valuation',
    icon: <DollarSign className="w-5 h-5" />,
    gradient: 'from-amber-500 to-orange-500',
  },
  market: {
    name: 'Market',
    icon: <LineChart className="w-5 h-5" />,
    gradient: 'from-purple-500 to-pink-500',
  },
  sentiment: {
    name: 'Sentiment',
    icon: <Users className="w-5 h-5" />,
    gradient: 'from-rose-500 to-red-500',
  },
  context: {
    name: 'Context',
    icon: <Globe className="w-5 h-5" />,
    gradient: 'from-cyan-500 to-teal-500',
  },
};

// Article Row Component
interface ArticleRowProps {
  article: ArticleSummary;
}

function ArticleRow({ article }: ArticleRowProps): ReactElement {
  return (
    <Link
      to={`/articles/${article.id}`}
      className="group block bg-slate-900/50 border border-slate-700/50 rounded-xl p-4 hover:bg-slate-900/80 hover:border-slate-600 transition-all"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Title */}
          <h3 className="text-base font-medium text-slate-200 group-hover:text-white transition-colors mb-1" style={{ fontFamily: 'var(--font-display)' }}>
            {article.title}
          </h3>

          {/* Subtitle */}
          {article.subtitle && (
            <p className="text-sm text-slate-500 mb-2">
              {article.subtitle}
            </p>
          )}

          {/* Tickers + Metadata */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5">
              {article.tickers.slice(0, 5).map((ticker) => (
                <span
                  key={ticker}
                  className="px-1.5 py-0.5 text-[10px] font-mono text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded"
                >
                  {ticker}
                </span>
              ))}
              {article.tickers.length > 5 && (
                <span className="text-[10px] text-slate-500">+{article.tickers.length - 5}</span>
              )}
            </div>
            <span className="text-[10px] text-slate-600">•</span>
            <span className="text-xs text-slate-500 capitalize">{article.provider}</span>
            <span className="text-[10px] text-slate-600">•</span>
            <span className="text-xs text-slate-500">{article.time_ago}</span>
          </div>
        </div>

        <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-slate-400 transition-colors flex-shrink-0" />
      </div>
    </Link>
  );
}

export default function CategoryList(): ReactElement {
  const { category } = useParams<{ category: string }>();
  const [page, setPage] = useState(0);
  const limit = 20;

  const categoryConfig = CATEGORY_CONFIG[category || ''] || CATEGORY_CONFIG.overview;

  // Fetch articles for this category
  const { data, isLoading, error } = useQuery<ArticleListResponse>({
    queryKey: ['articles-category', category, page],
    queryFn: async () => {
      const response = await articlesAPI.listArticles({
        category,
        limit,
        offset: page * limit,
      });
      return response.data;
    },
    enabled: !!category,
    staleTime: 2 * 60 * 1000,
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Background pattern */}
      <div className="fixed inset-0 opacity-40 pointer-events-none">
        <div className="absolute inset-0 grid-pattern" />
      </div>

      <div className="relative max-w-4xl mx-auto px-6 py-8">
        {/* Back Link */}
        <Link
          to="/articles"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-300 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Articles
        </Link>

        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center gap-4 mb-2">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${categoryConfig.gradient} flex items-center justify-center shadow-lg`}>
              <div className="text-white">{categoryConfig.icon}</div>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'var(--font-display)' }}>
                {categoryConfig.name}
              </h1>
              {data && (
                <p className="text-sm text-slate-500">
                  {data.total_count} article{data.total_count !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>
        </header>

        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-emerald-500 mb-4" />
            <p className="text-slate-400">Loading articles...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
              <FileText className="w-8 h-8 text-red-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-200 mb-2">Failed to load articles</h3>
            <p className="text-sm text-slate-500">Please try again later</p>
          </div>
        )}

        {/* Articles List */}
        {data && !isLoading && (
          <>
            <div className="space-y-3">
              {data.articles.map((article) => (
                <ArticleRow key={article.id} article={article} />
              ))}
            </div>

            {/* Empty State */}
            {data.articles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-800 flex items-center justify-center">
                  <FileText className="w-8 h-8 text-slate-600" />
                </div>
                <h3 className="text-lg font-medium text-slate-200 mb-2">No articles yet</h3>
                <p className="text-sm text-slate-500">Check back later for new content</p>
              </div>
            )}

            {/* Pagination */}
            {data.total_count > limit && (
              <div className="flex items-center justify-center gap-4 mt-8">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                <span className="text-sm text-slate-500">
                  Page {page + 1} of {Math.ceil(data.total_count / limit)}
                </span>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!data.has_more}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
